// Shapes shared by the backend and the web client. Android mirrors them in Kotlin
// (see android/app/src/main/java/app/vladrss/data/Model.kt); the sync endpoints
// return these field names unchanged.

export type ArticleKind = "article" | "comic" | "trail" | "video";
export type Filter = "unread" | "all" | "saved";
export type Order = "oldest" | "newest";
export type Grouping = "time" | "source";

/** A list row: everything a list or a count needs, never the body. */
export interface ArticleRow {
  id: string;
  feedId: string;
  url: string;
  title: string;
  author?: string;
  publishedAt: number;
  preview: string;
  imageUrl?: string;
  kind: ArticleKind;
  /** mainly a video (a player with little text, or titled "Video: …") */
  video?: boolean;
  topics?: string[]; // the followed topics (keys) it is filed under
  /** where a topic was found when the title does not name it (core topicMatch) */
  topicMatches?: { topic: string; text: string; start: number; end: number }[];
  isRead: boolean;
  isSaved: boolean;
  readAt?: number;
  savedAt?: number;
  /** Hidden, but retained for Undo and offline commands until the retry horizon passes. */
  retiredAt?: number;
}

export type Health =
  | { state: "ok"; note: string }
  | { state: "quiet"; weeks: number }
  | { state: "failing"; since: number; reason: string };

/** A scope is what the list shows: everything, a category, one source, a search, or history. */
export type Scope =
  | { type: "all" }
  | { type: "category"; name: string }
  | { type: "feed"; feedId: string }
  | { type: "topic"; name: string } // a followed word, by its name as written
  | { type: "search"; query: string }
  | { type: "recent" };

export const scopeKey = (s: Scope): string =>
  s.type === "all" ? "all"
  : s.type === "category" ? `cat:${s.name}`
  : s.type === "feed" ? `feed:${s.feedId}`
  : s.type === "topic" ? `topic:${s.name.toLowerCase()}`
  : s.type === "search" ? `q:${s.query}`
  : "recent";
